<template>
	<h-radio-group
		:id="`configurator-property-${productProperty.id}`"
		v-model="selected"
		class="space-y-xs rounded-2xs border border-solid border-gray-100 p-sm"
	>
		<p class="flex h-6 flex-row items-center">
			<span class="space-x-xs">
				<span>
					<span>{{ productProperty.title }}</span>

					<template v-if="selected">:</template>
				</span>

				<span v-if="selected" class="font-bold">
					{{ selected?.title }}
				</span>
			</span>

			<button
				v-if="selected && !productProperty.isRequired"
				type="button"
				class="ring-focus ml-auto flex size-6 items-center justify-center rounded-full text-red-400 hover:text-red-500 focus:rounded-2xs"
				@click="selected = undefined"
			>
				<v-icon icon="close" />
			</button>
		</p>

		<div
			:class="{
				'grid grid-cols-2 gap-xs lg:grid-cols-3': optionType === 'size',
				'my-xs flex flex-wrap pb-xs': optionType === 'color',
				'grid grid-cols-1': optionType === 'default',
			}"
		>
			<v-page-product-configurator-option
				v-for="option in productProperty.options"
				:key="option.id"
				:type="optionType"
				:value="option"
				:option="option"
			/>
		</div>
	</h-radio-group>
</template>

<script lang="ts" setup>
import type {ViewProductQuery} from "@pkg/shared/graphql";
import type {ProductKind} from "@pkg/shared/types";

type ProductProperty = ProductKind<
	ViewProductQuery["product"]
>["properties"][0];
type ProductPropertyOption = ProductProperty["options"][0];

const properties = defineProps<{
	modelValue?: string;
	productProperty: ProductProperty;
}>();

const emit = defineEmits<{
	(event: "update:modelValue", value: string | undefined): void;
}>();

const selected = computed<ProductPropertyOption | undefined>({
	get() {
		return properties.productProperty.options.find(
			(option) => option.id === properties.modelValue,
		);
	},
	set(value) {
		emit("update:modelValue", value?.id);
	},
});

const optionType = computed<"default" | "color" | "size">(() => {
	if (properties.productProperty.options.every((option) => !!option.color)) {
		return "color";
	}

	const isSizeAttribute = properties.productProperty.options
		.map((option) => option.title)
		.every((title) => title.match(/[\d\s+]x[\d\s]/));

	return isSizeAttribute ? "size" : "default";
});
</script>
